import React, { useState, useRef, useEffect, useCallback } from "react";
import { useI18n } from "../../lib/i18n";
import { Button } from "../ui/button";
import { File, FilePlus, Trash, MagnifyingGlass, Warning } from "@phosphor-icons/react";

// ---------------------------------------------------------------------------
// Monaco is loaded from CDN via a <script> tag injected once.
// We keep a module-level promise so multiple mounts share the same load.
// ---------------------------------------------------------------------------
let _monacoReady = null;
const MONACO_CDNS = [
  "https://cdn.jsdelivr.net/npm/monaco-editor@0.50.0/min/vs",
  "https://unpkg.com/monaco-editor@0.50.0/min/vs",
  "https://cdnjs.cloudflare.com/ajax/libs/monaco-editor/0.50.0/min/vs",
];

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) { resolve(); return; }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("script " + src));
    document.head.appendChild(s);
  });
}

function loadMonaco() {
  if (_monacoReady) return _monacoReady;
  _monacoReady = (async () => {
    if (window.monaco) return window.monaco;
    let lastErr;
    for (const base of MONACO_CDNS) {
      try {
        await loadScript(base + "/loader.js").catch(() => loadScript(base + "/loader.min.js"));
        const req = window.require;
        if (!req || typeof req.config !== "function") throw new Error("AMD loader missing");
        req.config({ paths: { vs: base } });
        await new Promise((resolve, reject) => {
          const t = setTimeout(() => reject(new Error("timeout")), 12000);
          req(["vs/editor/editor.main"], () => {
            clearTimeout(t);
            if (window.monaco) resolve();
            else reject(new Error("monaco missing"));
          });
        });
        return window.monaco;
      } catch (e) {
        lastErr = e;
      }
    }
    throw lastErr || new Error("Monaco CDN failed");
  })();
  return _monacoReady;
}

function languageFromPath(path) {
  if (!path) return "plaintext";
  if (path.endsWith(".js"))   return "javascript";
  if (path.endsWith(".ts"))   return "typescript";
  if (path.endsWith(".py"))   return "python";
  if (path.endsWith(".md"))   return "markdown";
  if (path.endsWith(".json")) return "json";
  if (path.endsWith(".html")) return "html";
  if (path.endsWith(".css"))  return "css";
  if (path.endsWith(".cs"))   return "csharp";
  if (path.endsWith(".gd"))   return "gdscript";  // Monaco treats as plaintext but label is correct
  if (path.endsWith(".lua"))  return "lua";
  return "plaintext";
}

// ---------------------------------------------------------------------------
// Monaco Editor wrapper component
// ---------------------------------------------------------------------------
function MonacoEditor({ value, language, onChange, readOnly = false, onFail }) {
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const modelRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize editor once
  useEffect(() => {
    let cancelled = false;
    loadMonaco()
      .then((monaco) => {
        if (cancelled || !containerRef.current) return;

        // Dispose previous editor if any
        if (editorRef.current) { editorRef.current.dispose(); editorRef.current = null; }

        editorRef.current = monaco.editor.create(containerRef.current, {
          value: value || "",
          language,
          theme: "vs-dark",
          fontSize: 13,
          fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
          lineHeight: 20,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          automaticLayout: true,
          tabSize: 2,
          wordWrap: "on",
          readOnly,
          padding: { top: 12, bottom: 12 },
          scrollbar: { verticalScrollbarSize: 6, horizontalScrollbarSize: 6 },
          suggestOnTriggerCharacters: true,
          quickSuggestions: true,
          parameterHints: { enabled: true },
          renderLineHighlight: "line",
          cursorBlinking: "smooth",
          smoothScrolling: true,
          bracketPairColorization: { enabled: true },
          guides: { bracketPairs: true },
        });

        // Override VS Dark background to match app
        monaco.editor.defineTheme("gf-dark", {
          base: "vs-dark",
          inherit: true,
          rules: [],
          colors: {
            "editor.background": "#0a0a0c",
            "editor.lineHighlightBackground": "#18181b",
            "editorLineNumber.foreground": "#3f3f46",
            "editorLineNumber.activeForeground": "#22d3ee",
            "editor.selectionBackground": "#22d3ee33",
          },
        });
        monaco.editor.setTheme("gf-dark");

        // Listen for content changes
        editorRef.current.onDidChangeModelContent(() => {
          if (onChange) onChange(editorRef.current.getValue());
        });

        setLoading(false);
      })
      .catch((e) => {
        console.error("Monaco load error", e);
        setError("No se pudo cargar Monaco.");
        onFail?.();
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // intentional: only on mount

  // Sync value externally when file switches
  useEffect(() => {
    if (!editorRef.current || !window.monaco) return;
    const current = editorRef.current.getValue();
    if (current !== value) {
      editorRef.current.setValue(value || "");
    }
  }, [value]);

  // Sync language when file switches
  useEffect(() => {
    if (!editorRef.current || !window.monaco) return;
    const model = editorRef.current.getModel();
    if (model) window.monaco.editor.setModelLanguage(model, language);
  }, [language]);

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-zinc-500 gap-3 p-8">
        <Warning size={28} className="text-yellow-400" />
        <div className="text-xs font-mono text-center">{error}</div>
        <div className="text-[10px] text-zinc-600 text-center max-w-xs">
          Usa el editor de texto simple como fallback mientras se resuelve la conexión.
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 relative overflow-hidden">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0c] z-10">
          <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 gf-blink">
            CARGANDO MONACO...
          </div>
        </div>
      )}
      <div ref={containerRef} style={{ height: "100%", width: "100%" }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fallback plain textarea (used when Monaco fails to load)
// ---------------------------------------------------------------------------
function PlainEditor({ value, onChange }) {
  return (
    <textarea
      data-testid="code-editor-textarea"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      spellCheck={false}
      className="flex-1 bg-[#0a0a0c] text-zinc-100 font-mono text-sm p-4 outline-none resize-none leading-relaxed gf-scroll"
      style={{ tabSize: 2 }}
    />
  );
}

// ---------------------------------------------------------------------------
// Main CodeEditorPanel
// ---------------------------------------------------------------------------
export default function CodeEditorPanel({ project, onChange }) {
  const { t } = useI18n();
  const files = project.files || [];
  const [activeId, setActiveId] = useState(files[0]?.id);
  const [useMonaco, setUseMonaco] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);

  const active = files.find((f) => f.id === activeId) || files[0];

  const updateContent = useCallback((val) => {
    const next = files.map((f) => (f.id === active?.id ? { ...f, content: val } : f));
    onChange({ files: next });
  }, [files, active, onChange]);

  const handleNew = () => {
    const name = window.prompt("Path (ej: src/enemy.js)", "src/new.js");
    if (!name) return;
    const id = crypto.randomUUID();
    const newFile = { id, path: name, language: languageFromPath(name), content: "" };
    onChange({ files: [...files, newFile] });
    setActiveId(id);
  };

  const handleDelete = () => {
    if (!active) return;
    if (!window.confirm(`¿Eliminar ${active.path}?`)) return;
    const next = files.filter((f) => f.id !== active.id);
    onChange({ files: next });
    setActiveId(next[0]?.id);
  };

  // Build directory tree
  const tree = {};
  files.forEach((f) => {
    const parts = f.path.split("/");
    let cur = tree;
    parts.slice(0, -1).forEach((p) => { cur[p] = cur[p] || {}; cur = cur[p]; });
    cur._files = cur._files || [];
    cur._files.push(f);
  });

  const renderNode = (node, depth = 0) => {
    const folders = Object.keys(node).filter((k) => k !== "_files");
    return (
      <>
        {folders.map((f) => (
          <div key={f}>
            <div
              className="px-3 py-1 text-[10px] tracking-[0.3em] uppercase text-zinc-500"
              style={{ paddingLeft: 12 + depth * 12 }}
            >
              {f}/
            </div>
            {renderNode(node[f], depth + 1)}
          </div>
        ))}
        {(node._files || []).map((f) => (
          <button
            key={f.id}
            data-testid={`file-tree-${f.id}`}
            onClick={() => setActiveId(f.id)}
            className={
              "w-full text-left flex items-center gap-2 text-xs px-3 py-1.5 font-mono truncate transition-colors " +
              (f.id === active?.id
                ? "bg-zinc-800 text-cyan-300"
                : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100")
            }
            style={{ paddingLeft: 12 + depth * 12 }}
          >
            <File size={12} />
            {f.path.split("/").slice(-1)[0]}
          </button>
        ))}
      </>
    );
  };

  const langLabel = active ? languageFromPath(active.path) : "";
  const lineCount = active?.content ? active.content.split("\n").length : 0;

  return (
    <div className="h-full flex">
      {/* File tree */}
      <aside className="w-64 border-r border-zinc-800 flex flex-col bg-zinc-950 overflow-hidden">
        <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">FILES</div>
          <div className="flex gap-1">
            <button
              onClick={() => setSearchOpen((s) => !s)}
              className="text-zinc-400 hover:text-cyan-400 p-1"
              title="Buscar en archivos"
            >
              <MagnifyingGlass size={14} />
            </button>
            <button
              data-testid="new-file-btn"
              onClick={handleNew}
              className="text-zinc-400 hover:text-cyan-400 p-1"
              title={t("code.newfile")}
            >
              <FilePlus size={14} />
            </button>
          </div>
        </div>
        {searchOpen && <FileSearch files={files} onSelect={setActiveId} />}
        <div className="flex-1 overflow-auto gf-scroll py-2">
          {files.length === 0 ? (
            <div className="px-4 py-8 text-center text-[10px] text-zinc-600 uppercase tracking-widest">
              Sin archivos.<br/>Crea uno con +
            </div>
          ) : (
            renderNode(tree)
          )}
        </div>
        {/* Editor toggle */}
        <div className="px-3 py-2 border-t border-zinc-800">
          <button
            onClick={() => setUseMonaco((m) => !m)}
            className="text-[10px] tracking-[0.25em] uppercase text-zinc-600 hover:text-zinc-400"
          >
            {useMonaco ? "// Monaco" : "// Textarea"} ↔ switch
          </button>
        </div>
      </aside>

      {/* Editor area */}
      <section className="flex-1 flex flex-col overflow-hidden">
        {/* Tab bar */}
        <div className="px-4 py-2 border-b border-zinc-800 flex items-center justify-between bg-zinc-950 min-h-[40px]">
          <div className="flex items-center gap-2 text-xs font-mono text-zinc-300 truncate">
            <File size={12} className="text-cyan-400 flex-shrink-0" />
            <span className="truncate">{active?.path || "—"}</span>
            {langLabel && (
              <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 ml-1 flex-shrink-0">
                {langLabel}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[10px] text-zinc-600 font-mono">{lineCount > 0 ? `${lineCount} líneas` : ""}</span>
            {active && (
              <Button
                data-testid="delete-file-btn"
                onClick={handleDelete}
                variant="ghost"
                className="rounded-none text-zinc-500 hover:text-red-400 text-xs gap-1 h-7 px-2"
              >
                <Trash size={12} /> {t("code.delete")}
              </Button>
            )}
          </div>
        </div>

        {active ? (
          useMonaco ? (
            <MonacoEditor
              key={active.id}
              value={active.content || ""}
              language={languageFromPath(active.path)}
              onChange={updateContent}
              onFail={() => setUseMonaco(false)}
            />
          ) : (
            <PlainEditor value={active.content || ""} onChange={updateContent} />
          )
        ) : (
          <div className="flex-1 flex items-center justify-center text-zinc-500 text-xs uppercase tracking-[0.3em]">
            // no file open
          </div>
        )}

        {/* Status bar */}
        <div className="px-4 py-1 border-t border-zinc-800 bg-zinc-950 flex items-center gap-4 text-[10px] font-mono text-zinc-600">
          <span>{useMonaco ? "Monaco Editor" : "Plain Editor"}</span>
          {active && <span>{languageFromPath(active.path)}</span>}
          <span className="ml-auto">Tab: 2 · UTF-8</span>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Simple in-panel file search
// ---------------------------------------------------------------------------
function FileSearch({ files, onSelect }) {
  const [query, setQuery] = useState("");
  const results = query.trim()
    ? files.filter((f) =>
        f.path.toLowerCase().includes(query.toLowerCase()) ||
        (f.content || "").toLowerCase().includes(query.toLowerCase())
      )
    : [];

  return (
    <div className="border-b border-zinc-800 bg-zinc-950">
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Buscar en archivos..."
        className="w-full bg-transparent px-3 py-2 text-xs font-mono text-zinc-200 outline-none border-b border-zinc-800 placeholder:text-zinc-600"
      />
      {results.length > 0 && (
        <div className="max-h-40 overflow-auto gf-scroll">
          {results.map((f) => (
            <button
              key={f.id}
              onClick={() => { onSelect(f.id); setQuery(""); }}
              className="w-full text-left px-3 py-1.5 text-[11px] font-mono text-zinc-400 hover:bg-zinc-800 truncate"
            >
              {f.path}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}